<div id="user-settings-branding" class="settings-panel" [class.mobile]="isMobile">
  <div class="head">
    <span class="material-icons go-back" (click)="goBack.emit()"> arrow_back </span>
    <div class="description">
      <div class="title">{{ panelData?.name }}</div>
      <div class="subtitle">{{ panelData?.description }}</div>
    </div>
    <span class="material-icons close" (click)="goBack.emit()"> close </span>
  </div>
  <div class="sub-head">
    {{ panelData?.description }}
  </div>
  <form [formGroup]="form" (submit)="submitChanges()">
    <div class="body">
      <div id="settings-panel-form">
        <!-- Logos and Images Section -->
        <div class="settings-subsection-container">
          <p class="settings-section-title">{{ 'Brand Identity' | translate }}</p>
          <div class="settings-subsection-content">
            <div formGroupName="settings" class="settings-subsection-form">
              <!-- Dynamic Asset Fields -->
              <div class="settings-item" *ngFor="let asset of brandingConfig.assets">
                <div class="settings-item-info">
                  <p class="settings-item-title">{{ asset.title }}</p>
                  <p class="settings-item-description">
                    {{ asset.description }}
                  </p>
                </div>
                <div class="settings-item-control">
                  <ng-container *ngIf="form.get('settings.' + asset.key) as assetControl">
                    <div
                      class="logo-upload-wrap"
                      [ngClass]="{
                        noImage: !assetControl.value,
                        square: asset.type === LogoType.SQUARE_LOGO
                      }"
                    >
                      <div
                        class="logo-placeholder"
                        *ngIf="!assetControl.value"
                        (click)="fileInput.click()"
                      >
                        <span class="placeholder-text">{{ 'Click to add' | translate }}</span>
                      </div>
                      <div
                        *ngIf="assetControl.value"
                        class="logo-upload-img"
                        (click)="fileInput.click()"
                      >
                        <img [src]="assetControl.value" [alt]="asset.title" />
                      </div>
                      <app-pencil-button
                        class="corner-btn"
                        [buttonType]="'button'"
                        [theme]="'icon'"
                        [icon]="assetControl.value ? 'close' : 'edit'"
                        [iconConfig]="{ type: 'material-icon' }"
                        (clickEvent)="
                          assetControl.value ? clearAsset(asset.type) : fileInput.click()
                        "
                      >
                      </app-pencil-button>
                      <input
                        #fileInput
                        type="file"
                        [accept]="ALLOWED_IMAGE_TYPES"
                        [id]="'file-input-' + asset.type"
                        class="d-none"
                        (change)="uploadLogo($event, asset.type)"
                      />
                    </div>
                  </ng-container>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dynamic Color Sections -->
        <div class="settings-subsection-container" *ngFor="let section of brandingConfig.sections">
          <p class="settings-section-title">{{ section.title }}</p>
          <div class="settings-subsection-content">
            <div formGroupName="settings" class="settings-subsection-form">
              <!-- Dynamic Color Fields -->
              <div class="settings-item" *ngFor="let field of section.fields">
                <div class="settings-item-info">
                  <p class="settings-item-title">{{ field.title }}</p>
                </div>
                <div class="settings-item-control">
                  <ng-container *ngIf="form.get('settings.' + field.key) as fieldControl">
                    <div class="pencil-input-wrapper">
                      <div class="color-input-wrapper">
                        <input
                          type="text"
                          [formControlName]="field.key"
                          class="pencil-input"
                          [placeholder]="'e.g., #2b2b2b' | translate"
                          [class.invalid]="fieldControl.invalid"
                          (input)="fieldControl.setValue($event.target.value)"
                        />
                        <input
                          type="color"
                          [formControlName]="field.key"
                          class="pencil-color-input"
                          [class.invalid]="fieldControl.invalid"
                          title="Color picker"
                          (input)="fieldControl.setValue($event.target.value)"
                        />
                      </div>
                      <div
                        class="pencil-input-error-message"
                        *ngIf="fieldControl.hasError('invalidCssColor')"
                      >
                        {{ field.errorMessage || brandingConfig.defaultErrorMessage }}
                      </div>
                    </div>
                  </ng-container>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Virtual Backgrounds Section -->
        <div class="settings-subsection-container">
          <p class="settings-section-title">{{ 'Virtual Background' | translate }}</p>
          <div class="settings-subsection-content vb-section">
            <p class="settings-item-title">{{ 'Institution backgrounds' | translate }}</p>
            <app-pencil-spinner
              *ngIf="isLoadingVirtualBackgrounds$ | async"
              [diameter]="32"
            ></app-pencil-spinner>

            <div class="vb-gallery" *ngIf="(isLoadingVirtualBackgrounds$ | async) === false">
              <ng-container
                *ngTemplateOutlet="
                  emptyPlaceholder;
                  context: { clickHandler: vbFileInput.click.bind(vbFileInput) }
                "
              ></ng-container>
              <input
                #vbFileInput
                type="file"
                [accept]="ALLOWED_IMAGE_TYPES"
                class="d-none"
                (change)="uploadVirtualBackground($event)"
              />

              <ng-container
                *ngFor="let item of form.get('settings.virtualBackgroundUrls')?.value || []"
              >
                <ng-container
                  *ngTemplateOutlet="imageItemWithDeleteButton; context: { item: item }"
                ></ng-container>
              </ng-container>
            </div>

            <app-user-settings-permissions-toggle
              [formGroup]="form"
              [options]="{
                setting: 'onlyAllowInstitutionVirtualBackgrounds',
                titleText: 'Only allow institution backgrounds' | translate,
                activeText:
                  'People in your institution cannot use default and custom virtual backgrounds'
                  | translate,
                inactiveText:
                  'People in your institution cannot use default and custom virtual backgrounds'
                  | translate
              }"
            >
            </app-user-settings-permissions-toggle>
          </div>
        </div>

        <!-- Reset Defaults Section -->
        <div class="settings-subsection-container">
          <p class="settings-section-title">{{ 'Restore Defaults' | translate }}</p>
          <div class="settings-subsection-content">
            <div class="settings-item">
              <div class="settings-item-info">
                <p class="settings-item-title">
                  {{ 'Reset branding colors to their default values' | translate }}
                </p>
              </div>
              <div class="settings-item-control">
                <app-pencil-button
                  theme="danger"
                  (clickEvent)="resetThemeColorsToDefaults()"
                  [buttonType]="'button'"
                  [label]="'Reset' | translate"
                >
                </app-pencil-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="foot" [class.show]="(formState$ | async) !== FormState.Initial">
      <app-pencil-button
        theme="primary"
        [buttonType]="'submit'"
        [disabled]="form.invalid || (formState$ | async) === FormState.Loading"
        [showSpinner]="(formState$ | async) === FormState.Loading"
        icon="check"
        [iconConfig]="{ type: 'material-icon' }"
        [label]="'Update' | translate"
      >
      </app-pencil-button>
      <app-pencil-button
        theme="secondary"
        [buttonType]="'button'"
        (clickEvent)="cancelChanges()"
        type="button"
        icon="close"
        [iconConfig]="{ type: 'material-icon' }"
        [label]="'Cancel' | translate"
      >
      </app-pencil-button>
    </div>
  </form>
</div>

<!-- Templates -->
<ng-template #emptyPlaceholder let-clickHandler="clickHandler">
  <div class="logo-upload-wrap noImage">
    <div class="logo-placeholder" (click)="clickHandler()">
      <span class="material-icons">add</span>
    </div>
  </div>
</ng-template>

<ng-template #imageItemWithDeleteButton let-item="item">
  <div class="vb-item">
    <img [src]="item.url" [alt]="item.name || 'Virtual Background'" loading="lazy" />
    <app-pencil-button
      class="corner-btn"
      [buttonType]="'button'"
      theme="icon"
      icon="delete"
      [iconConfig]="{ type: IconTypes.materialIcon, outlined: true }"
      (clickEvent)="deleteVirtualBackground(item)"
    ></app-pencil-button>
  </div>
</ng-template>
